/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    useState,
    useEffect,
    useMemo,
    useRef,
    useCallback,
    lazy,
    Suspense,
    type FocusEvent,
    type MouseEvent,
    type PointerEvent,
    type ReactElement,
} from 'react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { DynamicImage } from '@/components/dynamic-image';
import { preloadDynamicImage } from '@/components/dynamic-image/preload';
import ImageNavArrows from '@/components/image-nav-arrows';
import { useTranslation } from 'react-i18next';
import { useDeferredRenderSequence } from '@/hooks/use-deferred-render';
import { cn } from '@/lib/utils';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import type { AppConfig } from '@/types/config';
import { UITarget } from '@/targets/ui-target';

// Overlay: only mounted after the first zoom interaction (lazy-load rule for overlays).
const GalleryZoomDialog = lazy(() => import('./zoom-dialog'));

// Cursor-follow zoom factor for the desktop hover state.
const HOVER_ZOOM_SCALE = 1.8;

export interface GalleryImage {
    src: string;
    alt?: string;
    thumbSrc?: string;
    /**
     * External (non-DIS) media — e.g. Widen CDN URLs from the product record. When true, the item
     * renders as a plain responsive `<img>` (using `srcSet`/`sizes`) instead of `<DynamicImage>`.
     */
    external?: boolean;
    /** `srcset` for the main `<img>` when `external` (ignored for DIS items). */
    srcSet?: string;
    /** `sizes` for the main `<img>` when `external`. */
    sizes?: string;
    /**
     * Video file URL. When set, the main slot renders a `<video>` (with `src`/`thumbSrc` as its
     * poster) and the thumbnail shows the poster with a play affordance.
     */
    videoSrc?: string;
}

interface ImageGalleryWidths {
    /**
     * Responsive widths for the main image. Defaults match PDP (`section-container` → `lg:grid-cols-2` →
     * `max-w-screen-2xl`): viewport-wide below `lg`, half-viewport at `lg`/`xl`, capped at 680 once the
     * container hits `max-w-screen-2xl`. Override when the gallery sits in a narrower container (modal,
     * card cell) so DIS doesn't deliver an oversized main image.
     */
    main?: DynamicImageDimensions;
    /**
     * Responsive widths for thumbnails in grid mode. Defaults match PDP (gallery half-width at `lg+`,
     * grid-cols-4). Override when the gallery's effective width differs (e.g. inside a modal at `lg+` or
     * a multi-column card grid). Ignored when `horizontalThumbnails` is set — the strip uses fixed CSS
     * sizes instead.
     */
    thumbnail?: DynamicImageDimensions;
}

interface ImageGalleryProps {
    images: GalleryImage[];
    eager?: boolean;
    /** Show prev/next arrows on the main image (e.g. in modal) */
    showNavigationArrows?: boolean;
    /** Size of navigation arrows: "sm" (default) or "lg" for PDP */
    navigationArrowSize?: 'sm' | 'lg';
    /** Use horizontal scrollable thumbnail strip with arrows instead of grid */
    horizontalThumbnails?: boolean;
    /** Render thumbnails as a wrapping row of fixed 100px squares on a white background (PDP design). */
    fixedThumbnails?: boolean;
    productName?: string;
    /**
     * Per-callsite responsive widths for the main image and grid thumbnails. Either field may be
     * omitted to keep the corresponding default. Defaults are PDP-shaped (full container,
     * `lg:grid-cols-2`, capped at `max-w-screen-2xl`); override when the gallery sits in a narrower
     * container so DIS doesn't deliver oversized variants.
     */
    widths?: ImageGalleryWidths;
}

type NetworkInformation = {
    saveData?: boolean;
};

// Cap eager preloads so a set/bundle PDP (one gallery per child product) doesn't fan out into an unclear amount of
// requests on hydration. Slides beyond the cap are promoted on hover/focus intent before the click lands. Within the
// cap, the preloads are spread across consecutive idle frames so the network burst stays flat.
const EAGER_PRELOAD_LIMIT = 4;
const THUMBNAIL_SCROLL_OFFSET = 200;
// Fixed PDP thumbnail strip geometry (size-[100px] + gap-2) used to compute one-thumbnail slides.
const FIXED_THUMBNAIL_SIZE = 100;
const FIXED_THUMBNAIL_GAP = 8;

// === Canonical width ladder for cache reuse ===
//
// Each width here turns into a `sw=<n>` query param on the DIS URL, and `sw` is part of the cache key. A single
// browsing session typically hits the gallery on the PDP, the cart-edit modal, the bonus-product modal, and the
// per-child gallery on a set/bundle PDP. If those four surfaces pick four slightly different widths for the same
// product image, the browser and CDN cache them as four separate variants. Same picture, four downloads.
//
// Fix: pick widths from a small shared ladder instead of fitting each container to the pixel. ~3-5% over-supply is
// invisible to the user; the cache reuse compounds across navigation. When in doubt, round up to the next rung —
// never invent a new one.
//
//   thumbs:  64, 80, 96, 144, 176, 240
//   main:    360, 420, 680, '100vw' (resolves to 640/768/1024), '50vw'
//
// Alignments this currently buys us:
//   - All mobile main images share the `'100vw'` rung → 640/768/1024 on PDP, cart-modal, bonus-modal.
//   - All desktop "narrow gallery" main images share 420 (cart-modal `md+`, bonus-modal `lg+`, child-card `md+`).
//   - PDP and bonus-modal share the same `md` thumb at 240.
//
// PDP gallery is full-width below `lg`, half-width at `lg`/`xl` (`lg:grid-cols-2`), and capped at 680 once the
// container hits `max-w-screen-2xl`. The `'50vw'` overestimates slightly (ignores `lg:gap-12`) which is harmless —
// the browser picks the smallest sufficient candidate from the srcset.
const DEFAULT_WIDTHS_MAIN: DynamicImageDimensions = { base: '100vw', lg: '50vw', '2xl': 680 };
// Grid is `grid-cols-4 gap-2 sm:gap-3` inside the gallery container, and the gallery itself drops to half-width once
// PDP switches to `lg:grid-cols-2`. Cell width therefore peaks in the `md` range (~230px just below `lg`) and falls
// back to ~160px at `lg+` where the gallery is half-width and capped by `max-w-screen-2xl`. Each entry targets the
// upper end of its breakpoint range so DIS still has headroom at @2x DPR.
const DEFAULT_WIDTHS_THUMBNAIL_GRID: DynamicImageDimensions = { base: 144, sm: 176, md: 240, lg: 168 };
// Horizontal strip uses fixed `h-16 w-16 sm:h-20 sm:w-20` — 64 base, 80 sm+. Fixed CSS, so this is intentionally
// not configurable per consumer.
const DEFAULT_WIDTHS_THUMBNAIL_STRIP: DynamicImageDimensions = [64, 80];

/**
 * Respect the user-explicit Data Saver preference (`Save-Data`) by skipping preloads. Falls back to "preload allowed"
 * when the `NetworkInformation` API isn't exposed (Safari, Firefox). We deliberately do not consult `effectiveType`,
 * which is a passively-derived network signal and a known fingerprinting vector — only the explicit user preference is
 * honored. The remaining defenses against bandwidth waste (cap, idle scheduling, `fetchPriority: 'low'`) carry the rest.
 */
const isPreloadAllowedByConnection = (): boolean => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
    return !connection?.saveData;
};

/**
 * Renders the main gallery slot. External (Widen) items render a plain responsive `<img>`; video
 * items render a `<video>` with the poster; everything else goes through `<DynamicImage>` (DIS).
 */
function GalleryMainMedia({
    image,
    mainWidths,
    eager,
    videoLabel,
}: {
    image: GalleryImage;
    mainWidths: DynamicImageDimensions;
    eager: boolean;
    videoLabel: string;
}): ReactElement {
    // Gallery images are decorative — the whole gallery is aria-hidden (the product name already carries
    // the accessible label elsewhere), so images render an empty `alt` and no per-asset DAM alt is
    // fetched (UCR-2769). The video keeps a label since it's interactive media, not a still image.
    if (image.videoSrc) {
        return (
            <video
                className="h-full w-full object-contain"
                controls
                playsInline
                preload="none"
                poster={image.src || undefined}
                aria-label={videoLabel}>
                <source src={image.videoSrc} type="video/mp4" />
            </video>
        );
    }

    if (image.external) {
        return (
            <img
                src={image.src}
                srcSet={image.srcSet}
                sizes={image.sizes}
                alt=""
                className="h-full w-full object-cover object-center"
                loading={eager ? 'eager' : 'lazy'}
                fetchPriority={eager ? 'high' : undefined}
            />
        );
    }

    return (
        <DynamicImage
            src={image.src}
            alt=""
            widths={mainWidths}
            className="w-full h-full object-cover object-center [&_img]:object-contain! [&_img]:h-full! [&_img]:max-w-full! [&_img]:mx-auto!"
            loading={eager ? 'eager' : 'lazy'}
            priority={eager ? 'high' : undefined}
        />
    );
}

/**
 * Wraps the main image with the UCR-2769 zoom affordances: on hover-capable pointers the image scales
 * toward the cursor (transform-origin tracks the pointer); a click/tap opens the fullscreen pinch-zoom
 * lightbox. Rendered only for still images — video items keep their native controls.
 */
function MainImageZoom({
    image,
    mainWidths,
    eager,
    videoLabel,
    enableHoverZoom,
    onOpen,
    openLabel,
}: {
    image: GalleryImage;
    mainWidths: DynamicImageDimensions;
    eager: boolean;
    videoLabel: string;
    enableHoverZoom: boolean;
    onOpen: () => void;
    openLabel: string;
}): ReactElement {
    const frameRef = useRef<HTMLButtonElement>(null);
    const [zoomed, setZoomed] = useState(false);

    const handleMouseMove = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        const el = frameRef.current;
        if (!el) {
            return;
        }
        const rect = el.getBoundingClientRect();
        el.style.setProperty('--zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
        el.style.setProperty('--zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
    }, []);

    return (
        <button
            ref={frameRef}
            type="button"
            onClick={onOpen}
            onMouseEnter={enableHoverZoom ? () => setZoomed(true) : undefined}
            onMouseMove={enableHoverZoom ? handleMouseMove : undefined}
            onMouseLeave={enableHoverZoom ? () => setZoomed(false) : undefined}
            aria-label={openLabel}
            // Gallery is aria-hidden (UCR-2769) — keep its controls out of the keyboard tab order too.
            tabIndex={-1}
            className="block h-full w-full cursor-zoom-in">
            <div
                className="h-full w-full transition-transform duration-200 ease-out motion-reduce:transition-none"
                style={{
                    transformOrigin: 'var(--zoom-x, 50%) var(--zoom-y, 50%)',
                    transform: zoomed ? `scale(${HOVER_ZOOM_SCALE})` : 'scale(1)',
                }}>
                <GalleryMainMedia image={image} mainWidths={mainWidths} eager={eager} videoLabel={videoLabel} />
            </div>
        </button>
    );
}

/** Renders a thumbnail. External/video items use a plain `<img>` (poster + play glyph for video). */
function GalleryThumbMedia({
    image,
    thumbnailWidths,
}: {
    image: GalleryImage;
    thumbnailWidths: DynamicImageDimensions;
}): ReactElement {
    // Decorative — the gallery is aria-hidden (UCR-2769), so thumbnails carry an empty `alt`.
    if (image.external || image.videoSrc) {
        return (
            <div className="relative h-full w-full">
                <img
                    src={image.thumbSrc || image.src}
                    alt=""
                    className="h-full w-full object-cover object-center"
                    loading="lazy"
                />
                {image.videoSrc && (
                    <span className="absolute inset-0 flex items-center justify-center bg-bm-ink/30" aria-hidden="true">
                        <Play className="size-5 fill-bm-organic-white text-bm-organic-white" />
                    </span>
                )}
            </div>
        );
    }

    return (
        <DynamicImage
            src={image.thumbSrc || image.src}
            alt=""
            widths={thumbnailWidths}
            className="w-full h-full"
            imageProps={{ className: 'w-full h-full object-cover object-center' }}
            loading="lazy"
        />
    );
}

export default function ImageGallery({
    images,
    eager = false,
    showNavigationArrows = false,
    navigationArrowSize = 'sm',
    horizontalThumbnails = false,
    fixedThumbnails = false,
    productName,
    widths,
}: ImageGalleryProps): ReactElement {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const thumbStripRef = useRef<HTMLDivElement>(null);
    // Whether the fixed thumbnail strip can scroll further in each direction. Drives the overflow
    // navigation arrows (only shown when the thumbnails exceed the container).
    const [thumbOverflow, setThumbOverflow] = useState({ left: false, right: false });
    // Desktop hover-zoom only applies to hover-capable, fine pointers; touch devices open the lightbox.
    const [canHover, setCanHover] = useState(false);
    // The fullscreen pinch-zoom lightbox is mounted lazily on first open (overlay lazy-load rule).
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxMounted, setLightboxMounted] = useState(false);
    const config = useConfig<AppConfig>();
    const mainWidths = widths?.main ?? DEFAULT_WIDTHS_MAIN;
    const thumbnailWidths = widths?.thumbnail ?? DEFAULT_WIDTHS_THUMBNAIL_GRID;

    const preloadGallerySlide = useCallback(
        (image: GalleryImage | undefined) => {
            if (!image || image.external || image.videoSrc) {
                // External (Widen) and video items aren't served through DIS — nothing to preload here.
                return;
            }
            preloadDynamicImage({
                config,
                src: image.src,
                widths: mainWidths,
                fetchPriority: 'low',
            });
        },
        [config, mainWidths]
    );

    /**
     * Promote a non-eagerly-preloaded slide on hover/focus intent. By the time the click lands, the variant should
     * usually already be in the HTTP cache.
     */
    const handleThumbnailIntent = useCallback(
        (event: PointerEvent<HTMLButtonElement> | FocusEvent<HTMLButtonElement>) => {
            const index = Number(event.currentTarget.dataset.index);
            if (Number.isNaN(index)) {
                return;
            }
            preloadGallerySlide(images[index]);
        },
        [images, preloadGallerySlide]
    );
    const handleScrollThumbnailsLeft = useCallback(() => {
        thumbStripRef.current?.scrollBy({ left: -THUMBNAIL_SCROLL_OFFSET, behavior: 'smooth' });
    }, []);
    const handleScrollThumbnailsRight = useCallback(() => {
        thumbStripRef.current?.scrollBy({ left: THUMBNAIL_SCROLL_OFFSET, behavior: 'smooth' });
    }, []);

    // Recompute whether the fixed strip overflows the container in each direction. Called on scroll,
    // resize, and whenever the image set changes (a shorter set may no longer overflow).
    const updateThumbOverflow = useCallback(() => {
        const strip = thumbStripRef.current;
        if (!strip) {
            return;
        }
        const maxScroll = strip.scrollWidth - strip.clientWidth;
        setThumbOverflow({
            left: strip.scrollLeft > 1,
            right: strip.scrollLeft < maxScroll - 1,
        });
    }, []);

    // Page the fixed strip by as many whole thumbnails as currently fit in view.
    const handleThumbPage = useCallback((direction: 1 | -1) => {
        const strip = thumbStripRef.current;
        if (!strip) {
            return;
        }
        const step = FIXED_THUMBNAIL_SIZE + FIXED_THUMBNAIL_GAP;
        const perView = Math.max(1, Math.floor(strip.clientWidth / step));
        strip.scrollBy({ left: direction * step * perView, behavior: 'smooth' });
    }, []);
    const handleThumbnailClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        const nextIndex = Number(event.currentTarget.dataset.index);
        if (!Number.isNaN(nextIndex)) {
            setSelectedImageIndex(nextIndex);
        }
    }, []);

    const openLightbox = useCallback(() => {
        setLightboxMounted(true);
        setLightboxOpen(true);
    }, []);

    // Detect hover-capable pointers so hover-zoom is only wired on desktop; hybrid devices update live.
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }
        const query = window.matchMedia('(hover: hover) and (pointer: fine)');
        const update = () => setCanHover(query.matches);
        update();
        query.addEventListener('change', update);
        return () => query.removeEventListener('change', update);
    }, []);

    // Fixed-strip (PDP) thumbnail click: select the image, and keep a one-thumbnail buffer at each
    // edge — clicking the second-to-last visible thumb slides the strip one to the left (revealing the
    // next), and clicking the second visible thumb slides it one to the right (revealing the previous).
    const handleFixedThumbnailClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        const index = Number(event.currentTarget.dataset.index);
        if (Number.isNaN(index)) {
            return;
        }
        setSelectedImageIndex(index);
        const strip = thumbStripRef.current;
        if (!strip) {
            return;
        }
        const step = FIXED_THUMBNAIL_SIZE + FIXED_THUMBNAIL_GAP;
        const perView = Math.max(1, Math.round(strip.clientWidth / step));
        const firstVisible = Math.round(strip.scrollLeft / step);
        const lastVisible = firstVisible + perView - 1;
        if (index >= lastVisible - 1) {
            strip.scrollBy({ left: step, behavior: 'smooth' });
        } else if (index <= firstVisible + 1 && strip.scrollLeft > 0) {
            strip.scrollBy({ left: -step, behavior: 'smooth' });
        }
    }, []);

    useEffect(() => {
        // When images change (e.g., color variant changes), try to preserve the selected index
        // Only reset to 0 if the current index is out of bounds for the new images array
        // The key prop ensures each product has independent state, so this only affects the current product
        setSelectedImageIndex((currentIndex) => {
            // If current index is still valid for the new images array, keep it
            // Otherwise reset to 0
            return currentIndex < images.length ? currentIndex : 0;
        });
    }, [images]);

    // Keep the fixed-strip overflow arrows in sync with the container size and the image set. A
    // ResizeObserver catches container width changes; the `images` dependency re-measures when the
    // strip's content width changes (e.g. a color variant with fewer images).
    useEffect(() => {
        const strip = thumbStripRef.current;
        if (!strip || !fixedThumbnails) {
            return;
        }
        updateThumbOverflow();
        const observer = new ResizeObserver(updateThumbOverflow);
        observer.observe(strip);
        return () => observer.disconnect();
    }, [fixedThumbnails, images, updateThumbOverflow]);

    // Off-screen slides eligible for eager preload. Save-Data and the cap are folded into the array length so the
    // sequencer's `n === 0` disabled state doubles as the "nothing to do" guard. No idle frames are scheduled when
    // there's only one slide or when the user opted into Data Saver.
    const offScreenSlides = useMemo(
        () => (isPreloadAllowedByConnection() ? images.slice(1, 1 + EAGER_PRELOAD_LIMIT) : []),
        [images]
    );

    // Spread preloads across consecutive idle frames instead of registering all `EAGER_PRELOAD_LIMIT` in one tick.
    // `react-dom` dedupes per resource, so re-running the effect with a growing cursor is cheap. Slides beyond the
    // cap are promoted on hover/focus.
    const readyPreloadCount = useDeferredRenderSequence(offScreenSlides.length);
    useEffect(() => {
        offScreenSlides.slice(0, readyPreloadCount).forEach(preloadGallerySlide);
    }, [offScreenSlides, readyPreloadCount, preloadGallerySlide]);

    const { t: tCommon } = useTranslation('common');
    const { t: tProduct } = useTranslation('product');

    // The first image is the fallback image. It's needed for when `images` are just updated, and the `selectedImageIndex` goes out of bound and is soon to be reset.
    const selectedImage = images[selectedImageIndex] ?? images[0];

    if (!images || images.length === 0) {
        return (
            <div className="aspect-square bg-muted rounded-none flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                    <div className="text-4xl mb-2">📷</div>
                    <p>{tCommon('noImageAvailable')}</p>
                </div>
            </div>
        );
    }

    const imageAltFallback = productName || tProduct('imageAlt') || 'Product Image';

    return (
        <UITarget targetId="sfcc.pdp.products.gallery">
            {/* Decorative widget: the product name already labels the page, so the gallery is hidden from
                assistive tech (UCR-2769). Its images carry empty alt and no per-asset DAM alt is fetched. */}
            <div className="space-y-4" aria-hidden="true">
                {/* Main Image */}
                <div className="relative aspect-square overflow-hidden rounded-none bg-muted">
                    {selectedImage.videoSrc ? (
                        <GalleryMainMedia
                            image={selectedImage}
                            mainWidths={mainWidths}
                            eager={eager}
                            videoLabel={imageAltFallback}
                        />
                    ) : (
                        <MainImageZoom
                            image={selectedImage}
                            mainWidths={mainWidths}
                            eager={eager}
                            videoLabel={imageAltFallback}
                            enableHoverZoom={canHover}
                            onOpen={openLightbox}
                            openLabel={tProduct('zoomImage', { defaultValue: 'Zoom image' })}
                        />
                    )}
                    {showNavigationArrows && images.length > 1 && (
                        <ImageNavArrows
                            imageCount={images.length}
                            onIndexChange={setSelectedImageIndex}
                            size={navigationArrowSize}
                            tabIndex={-1}
                        />
                    )}
                </div>

                {/* Thumbnail Navigation */}
                {images.length > 1 && !horizontalThumbnails && !fixedThumbnails && (
                    <div className="grid grid-cols-4 gap-2 sm:gap-3">
                        {images.map((image, index) => (
                            <button
                                key={image.src + (image.thumbSrc || '')}
                                onClick={handleThumbnailClick}
                                onPointerEnter={handleThumbnailIntent}
                                onFocus={handleThumbnailIntent}
                                data-index={index}
                                tabIndex={-1}
                                className={cn(
                                    'aspect-square overflow-hidden rounded-none bg-muted border-2 transition-colors cursor-pointer',
                                    selectedImageIndex === index
                                        ? 'border-primary'
                                        : 'border-transparent hover:border-border'
                                )}>
                                <GalleryThumbMedia image={image} thumbnailWidths={thumbnailWidths} />
                            </button>
                        ))}
                    </div>
                )}

                {/* Fixed-size scrollable thumbnail strip (PDP): no wrap, no visible scrollbar. Clicking a
                    thumbnail near an edge slides the strip by one so a neighbor peeks into view. Prev/next
                    arrows overlay the edges only when the thumbnails overflow the container. */}
                {images.length > 1 && fixedThumbnails && (
                    <div className="relative">
                        <div
                            ref={thumbStripRef}
                            onScroll={updateThumbOverflow}
                            className="flex gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                            {images.map((image, index) => (
                                <button
                                    key={image.src + (image.thumbSrc || '')}
                                    onClick={handleFixedThumbnailClick}
                                    onPointerEnter={handleThumbnailIntent}
                                    onFocus={handleThumbnailIntent}
                                    data-index={index}
                                    tabIndex={-1}
                                    className={cn(
                                        'size-[100px] shrink-0 overflow-hidden rounded-none bg-bm-white border-2 transition-colors cursor-pointer',
                                        selectedImageIndex === index
                                            ? 'border-primary'
                                            : 'border-transparent hover:border-border'
                                    )}>
                                    <GalleryThumbMedia image={image} thumbnailWidths={thumbnailWidths} />
                                </button>
                            ))}
                        </div>
                        {thumbOverflow.left && (
                            <button
                                type="button"
                                onClick={() => handleThumbPage(-1)}
                                tabIndex={-1}
                                aria-label={tCommon('previousImage')}
                                className={cn(
                                    // Desktop only — mobile users scroll/swipe the strip instead.
                                    'absolute left-0 top-1/2 hidden size-9 -translate-y-1/2 items-center justify-center lg:flex',
                                    'rounded-none bg-bm-organic-white/90 text-bm-ink shadow-md transition-colors hover:bg-bm-organic-white',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                )}>
                                <ChevronLeft className="size-5" />
                            </button>
                        )}
                        {thumbOverflow.right && (
                            <button
                                type="button"
                                onClick={() => handleThumbPage(1)}
                                tabIndex={-1}
                                aria-label={tCommon('nextImage')}
                                className={cn(
                                    // Desktop only — mobile users scroll/swipe the strip instead.
                                    'absolute right-0 top-1/2 hidden size-9 -translate-y-1/2 items-center justify-center lg:flex',
                                    'rounded-none bg-bm-organic-white/90 text-bm-ink shadow-md transition-colors hover:bg-bm-organic-white',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                )}>
                                <ChevronRight className="size-5" />
                            </button>
                        )}
                    </div>
                )}

                {/* Horizontal Scrollable Thumbnail Strip */}
                {images.length > 1 && horizontalThumbnails && (
                    <div className="relative flex items-center gap-2">
                        {images.length > 4 && (
                            <button
                                type="button"
                                onClick={handleScrollThumbnailsLeft}
                                tabIndex={-1}
                                className={cn(
                                    'hidden sm:flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-none border border-border bg-background shadow-md',
                                    'hover:bg-muted transition-colors',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                )}
                                aria-label={tCommon('previousImage')}>
                                <ChevronLeft className="size-4" />
                            </button>
                        )}
                        <div
                            ref={thumbStripRef}
                            className="flex flex-1 gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                            {images.map((image, index) => (
                                <button
                                    key={image.src + (image.thumbSrc || '')}
                                    onClick={handleThumbnailClick}
                                    onPointerEnter={handleThumbnailIntent}
                                    onFocus={handleThumbnailIntent}
                                    data-index={index}
                                    tabIndex={-1}
                                    className={cn(
                                        'flex-shrink-0 h-16 w-16 sm:h-20 sm:w-20 overflow-hidden rounded-none bg-muted',
                                        'border-2 transition-colors cursor-pointer',
                                        selectedImageIndex === index
                                            ? 'border-primary'
                                            : 'border-transparent hover:border-border'
                                    )}>
                                    <GalleryThumbMedia image={image} thumbnailWidths={DEFAULT_WIDTHS_THUMBNAIL_STRIP} />
                                </button>
                            ))}
                        </div>
                        {images.length > 4 && (
                            <button
                                type="button"
                                onClick={handleScrollThumbnailsRight}
                                tabIndex={-1}
                                className={cn(
                                    'hidden sm:flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-none border border-border bg-background shadow-md',
                                    'hover:bg-muted transition-colors',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                )}
                                aria-label={tCommon('nextImage')}>
                                <ChevronRight className="size-4" />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {lightboxMounted && !selectedImage.videoSrc && (
                <Suspense fallback={null}>
                    <GalleryZoomDialog
                        open={lightboxOpen}
                        onOpenChange={setLightboxOpen}
                        src={selectedImage.src}
                        srcSet={selectedImage.srcSet}
                        sizes="100vw"
                        alt=""
                        title={imageAltFallback}
                    />
                </Suspense>
            )}
        </UITarget>
    );
}
